/**
 * Job Retention Section
 * Sub-component of PreferencesModal for all days_*_before_* numeric inputs for phase durations.
 */
import { useState, useEffect } from 'react';
import type { UserPreferences } from '../../types';

// Type for all App Options preference keys
type AppOptionsPrefKey =
  | 'days_queued_before_expired' | 'days_generating_before_expired' | 'days_ready_before_expired'
  | 'days_applied_before_expired' | 'days_followup_before_expired' | 'days_negotiation_before_expired' | 'days_accepted_before_expired'
  | 'days_skipped_before_trashed' | 'days_errored_before_trashed' | 'days_expired_before_trashed'
  | 'days_trash_before_deleted';

export interface JobRetentionSectionProps {
  preferences: UserPreferences;
  onPreferencesChange: (updated: UserPreferences) => void;
}

export function JobRetentionSection({ preferences, onPreferencesChange }: JobRetentionSectionProps) {
  // Temporary string values for inputs (allows free typing before validation)
  // Active Phases
  const [daysQueuedBeforeExpiredInput, setDaysQueuedBeforeExpiredInput] = useState('');
  const [daysGeneratingBeforeExpiredInput, setDaysGeneratingBeforeExpiredInput] = useState('');
  const [daysReadyBeforeExpiredInput, setDaysReadyBeforeExpiredInput] = useState('');
  const [daysAppliedBeforeExpiredInput, setDaysAppliedBeforeExpiredInput] = useState('');
  const [daysFollowupBeforeExpiredInput, setDaysFollowupBeforeExpiredInput] = useState('');
  const [daysNegotiationBeforeExpiredInput, setDaysNegotiationBeforeExpiredInput] = useState('');
  const [daysAcceptedBeforeExpiredInput, setDaysAcceptedBeforeExpiredInput] = useState('');
  // Terminal Phases
  const [daysSkippedBeforeTrashedInput, setDaysSkippedBeforeTrashedInput] = useState('');
  const [daysErroredBeforeTrashedInput, setDaysErroredBeforeTrashedInput] = useState('');
  const [daysExpiredBeforeTrashedInput, setDaysExpiredBeforeTrashedInput] = useState('');
  const [daysTrashBeforeDeletedInput, setDaysTrashBeforeDeletedInput] = useState('');

  // Sync input strings from preferences when they change externally (e.g., on load)
  useEffect(() => {
    setDaysQueuedBeforeExpiredInput(String(preferences.days_queued_before_expired ?? 90));
    setDaysGeneratingBeforeExpiredInput(String(preferences.days_generating_before_expired ?? 90));
    setDaysReadyBeforeExpiredInput(String(preferences.days_ready_before_expired ?? 90));
    setDaysAppliedBeforeExpiredInput(String(preferences.days_applied_before_expired ?? 90));
    setDaysFollowupBeforeExpiredInput(String(preferences.days_followup_before_expired ?? 90));
    setDaysNegotiationBeforeExpiredInput(String(preferences.days_negotiation_before_expired ?? 90));
    setDaysAcceptedBeforeExpiredInput(String(preferences.days_accepted_before_expired ?? 365));
    setDaysSkippedBeforeTrashedInput(String(preferences.days_skipped_before_trashed ?? 10));
    setDaysErroredBeforeTrashedInput(String(preferences.days_errored_before_trashed ?? 10));
    setDaysExpiredBeforeTrashedInput(String(preferences.days_expired_before_trashed ?? 30));
    setDaysTrashBeforeDeletedInput(String(preferences.days_trash_before_deleted ?? 30));
  }, [
    preferences.days_queued_before_expired,
    preferences.days_generating_before_expired,
    preferences.days_ready_before_expired,
    preferences.days_applied_before_expired,
    preferences.days_followup_before_expired,
    preferences.days_negotiation_before_expired,
    preferences.days_accepted_before_expired,
    preferences.days_skipped_before_trashed,
    preferences.days_errored_before_trashed,
    preferences.days_expired_before_trashed,
    preferences.days_trash_before_deleted,
  ]);

  // Determine max range based on status
  const userStatus = preferences.status;
  const userPlan = preferences.plan;
  const userRole = preferences.role;
  const isFullAccessUser = userRole === 'admin' || userRole === 'snappy' || userPlan === 'free' || userStatus === 'active';
  const maxDaysActive = isFullAccessUser ? 9999 : 90;
  const maxDaysAccepted = isFullAccessUser ? 9999 : 180;
  const maxDaysTerminal = isFullAccessUser ? 9999 : 30;

  // Helper to validate numeric input on blur and clamp to bounds
  function validateAndSetDays(
    inputValue: string,
    min: number,
    max: number,
    defaultVal: number,
    prefKey: AppOptionsPrefKey,
    setInputFn: (v: string) => void
  ) {
    const parsed = parseInt(inputValue, 10);
    if (isNaN(parsed)) {
      setInputFn(String(defaultVal));
      onPreferencesChange({ ...preferences, [prefKey]: defaultVal });
    } else {
      const clamped = Math.min(max, Math.max(min, parsed));
      setInputFn(String(clamped));
      onPreferencesChange({ ...preferences, [prefKey]: clamped });
    }
  }

  return (
    <div className="p-3 space-y-3">
      {/* Days Queued Before Expired */}
      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">Days in 'Queued' before Expired</span>
          <span className="text-xs text-gray-400">1-{maxDaysActive} days</span>
        </div>
        <input
          type="text"
          inputMode="numeric"
          value={daysQueuedBeforeExpiredInput}
          onChange={(e) => setDaysQueuedBeforeExpiredInput(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={() => validateAndSetDays(daysQueuedBeforeExpiredInput, 1, maxDaysActive, 90, 'days_queued_before_expired', setDaysQueuedBeforeExpiredInput)}
          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Days Generating Before Expired */}
      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">Days in 'Generating' before Expired</span>
          <span className="text-xs text-gray-400">1-{maxDaysActive} days</span>
        </div>
        <input
          type="text"
          inputMode="numeric"
          value={daysGeneratingBeforeExpiredInput}
          onChange={(e) => setDaysGeneratingBeforeExpiredInput(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={() => validateAndSetDays(daysGeneratingBeforeExpiredInput, 1, maxDaysActive, 90, 'days_generating_before_expired', setDaysGeneratingBeforeExpiredInput)}
          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Days Ready Before Expired */}
      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">Days in 'Ready' before Expired</span>
          <span className="text-xs text-gray-400">1-{maxDaysActive} days</span>
        </div>
        <input
          type="text"
          inputMode="numeric"
          value={daysReadyBeforeExpiredInput}
          onChange={(e) => setDaysReadyBeforeExpiredInput(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={() => validateAndSetDays(daysReadyBeforeExpiredInput, 1, maxDaysActive, 90, 'days_ready_before_expired', setDaysReadyBeforeExpiredInput)}
          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Days Applied Before Expired */}
      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">Days in 'Applied' before Expired</span>
          <span className="text-xs text-gray-400">1-{maxDaysActive} days</span>
        </div>
        <input
          type="text"
          inputMode="numeric"
          value={daysAppliedBeforeExpiredInput}
          onChange={(e) => setDaysAppliedBeforeExpiredInput(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={() => validateAndSetDays(daysAppliedBeforeExpiredInput, 1, maxDaysActive, 90, 'days_applied_before_expired', setDaysAppliedBeforeExpiredInput)}
          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Days Follow-Up Before Expired */}
      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">Days in 'Follow-Up' before Expired</span>
          <span className="text-xs text-gray-400">1-{maxDaysActive} days</span>
        </div>
        <input
          type="text"
          inputMode="numeric"
          value={daysFollowupBeforeExpiredInput}
          onChange={(e) => setDaysFollowupBeforeExpiredInput(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={() => validateAndSetDays(daysFollowupBeforeExpiredInput, 1, maxDaysActive, 90, 'days_followup_before_expired', setDaysFollowupBeforeExpiredInput)}
          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Days Negotiation Before Expired */}
      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">Days in 'Negotiation' before Expired</span>
          <span className="text-xs text-gray-400">1-{maxDaysActive} days</span>
        </div>
        <input
          type="text"
          inputMode="numeric"
          value={daysNegotiationBeforeExpiredInput}
          onChange={(e) => setDaysNegotiationBeforeExpiredInput(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={() => validateAndSetDays(daysNegotiationBeforeExpiredInput, 1, maxDaysActive, 90, 'days_negotiation_before_expired', setDaysNegotiationBeforeExpiredInput)}
          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Days Accepted Before Expired */}
      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">Days in 'Accepted' before Expired</span>
          <span className="text-xs text-gray-400">1-{maxDaysAccepted} days</span>
        </div>
        <input
          type="text"
          inputMode="numeric"
          value={daysAcceptedBeforeExpiredInput}
          onChange={(e) => setDaysAcceptedBeforeExpiredInput(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={() => validateAndSetDays(daysAcceptedBeforeExpiredInput, 1, maxDaysAccepted, 365, 'days_accepted_before_expired', setDaysAcceptedBeforeExpiredInput)}
          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Separator */}
      <hr className="border-gray-300 my-3" />

      {/* Days Skipped Before Trashed */}
      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">Days in 'Skipped' before Trashed</span>
          <span className="text-xs text-gray-400">1-{maxDaysTerminal} days</span>
        </div>
        <input
          type="text"
          inputMode="numeric"
          value={daysSkippedBeforeTrashedInput}
          onChange={(e) => setDaysSkippedBeforeTrashedInput(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={() => validateAndSetDays(daysSkippedBeforeTrashedInput, 1, maxDaysTerminal, 10, 'days_skipped_before_trashed', setDaysSkippedBeforeTrashedInput)}
          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Days Errored Before Trashed */}
      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">Days in 'Errored' before Trashed</span>
          <span className="text-xs text-gray-400">0-{maxDaysTerminal} days</span>
        </div>
        <input
          type="text"
          inputMode="numeric"
          value={daysErroredBeforeTrashedInput}
          onChange={(e) => setDaysErroredBeforeTrashedInput(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={() => validateAndSetDays(daysErroredBeforeTrashedInput, 0, maxDaysTerminal, 10, 'days_errored_before_trashed', setDaysErroredBeforeTrashedInput)}
          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Days Expired Before Trashed */}
      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">Days in 'Expired' before Trashed</span>
          <span className="text-xs text-gray-400">0-{maxDaysTerminal} days</span>
        </div>
        <input
          type="text"
          inputMode="numeric"
          value={daysExpiredBeforeTrashedInput}
          onChange={(e) => setDaysExpiredBeforeTrashedInput(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={() => validateAndSetDays(daysExpiredBeforeTrashedInput, 0, maxDaysTerminal, 30, 'days_expired_before_trashed', setDaysExpiredBeforeTrashedInput)}
          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Days Trash Before Deleted */}
      <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
        <div className="flex flex-col">
          <span className="text-sm text-gray-700">Days in 'Trash' before Deleted</span>
          <span className="text-xs text-gray-400">0-{maxDaysTerminal} days</span>
        </div>
        <input
          type="text"
          inputMode="numeric"
          value={daysTrashBeforeDeletedInput}
          onChange={(e) => setDaysTrashBeforeDeletedInput(e.target.value)}
          onFocus={(e) => e.target.select()}
          onBlur={() => validateAndSetDays(daysTrashBeforeDeletedInput, 0, maxDaysTerminal, 30, 'days_trash_before_deleted', setDaysTrashBeforeDeletedInput)}
          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:ring-primary-500 focus:border-primary-500"
        />
      </div>
    </div>
  );
}
